<script setup lang="ts">
import { computed, ref, shallowRef, watch } from 'vue'
import AppMessage from '../AppMessage/AppMessage.vue'
import AppResponse from '../AppResponse/AppResponse.vue'
import ActivityThread from '../ActivityThread/ActivityThread.vue'
import AppSources from '../AppSources/AppSources.vue'
import AppOutcomes from '../AppOutcomes/AppOutcomes.vue'
import AgentAsk from '../AgentAsk/AgentAsk.vue'
import DesignEditCard from '../DesignEditCard/DesignEditCard.vue'
import { startedDesignEdits, withoutDesignEdits } from './chat-outcomes'
import { useI18n } from 'vue-i18n'
import { askFromAction, askFromConnect, askFromQuestion } from '@/lib/chat/agent-ask'
import { isPendingMessage } from '@/types/chat'
import { buildTranscriptRows } from './transcript-rows'
import type { ActivityItem } from '../ActivityThread/activity-thread-types'
import type {
  AgentOutcome,
  ChatMessage,
  PendingAction,
  PendingConnect,
  PendingQuestion,
} from '@/types/chat'

const props = withDefaults(
  defineProps<{
    messages?: ChatMessage[]
    liveSteps?: ActivityItem[]
    streamingText?: string
    streaming?: boolean
    /** An ask just answered, filed here while its outcome is still fresh. Never an open one. */
    settledAction?: PendingAction
    /** A question just answered, filed here while its outcome is still fresh. Never an open one. */
    settledQuestion?: PendingQuestion
    /** A connect request just resolved, filed here while its outcome is still fresh. Never an open one. */
    settledConnect?: PendingConnect
    /** Outcome rows a tool_result chunk carried this turn, in arrival order. */
    liveOutcomes?: AgentOutcome[]
  }>(),
  {
    messages: () => [],
    liveSteps: () => [],
    streamingText: '',
    streaming: false,
    settledAction: undefined,
    settledQuestion: undefined,
    settledConnect: undefined,
    liveOutcomes: () => [],
  },
)

defineEmits<{
  openKnowledge: [slug: string]
}>()

const { t } = useI18n()

const translate = (key: string, named?: Record<string, unknown>) => (named ? t(key, named) : t(key))

const settledConnectProgress = { connecting: false } as const

const settledActionAsk = computed(() =>
  props.settledAction ? askFromAction(props.settledAction, translate) : undefined,
)

const settledQuestionAsk = computed(() =>
  props.settledQuestion ? askFromQuestion(props.settledQuestion, translate) : undefined,
)

const settledConnectAsk = computed(() =>
  props.settledConnect
    ? askFromConnect(props.settledConnect, settledConnectProgress, translate)
    : undefined,
)

const endedSteps = ref<ActivityItem[]>([])

watch(
  () => props.liveSteps,
  (steps) => {
    if (steps.length) endedSteps.value = steps
  },
  { immediate: true },
)

watch(
  () => props.streaming,
  (streaming) => {
    if (streaming) endedSteps.value = []
  },
)

watch(
  () => props.messages[0]?.threadId,
  () => {
    if (!props.streaming) endedSteps.value = []
  },
)

let turnCount = 0
const turnKey = ref(`activity:turn:${turnCount}`)
const keyAliases = shallowRef<ReadonlyMap<string, string>>(new Map())

watch(
  () => props.streaming,
  (streaming) => {
    if (streaming) turnKey.value = `activity:turn:${++turnCount}`
  },
)

watch(
  () => props.liveSteps[0]?.id,
  (id) => {
    if (id) keyAliases.value = new Map(keyAliases.value).set(id, turnKey.value)
  },
  { immediate: true },
)

watch(
  () => props.messages[0]?.threadId,
  () => {
    keyAliases.value = new Map()
  },
)

const liveOutcomeRows = computed(() => withoutDesignEdits(props.liveOutcomes))
const liveDesignEdits = computed(() => startedDesignEdits(props.liveOutcomes))

const rows = computed(() =>
  buildTranscriptRows({
    messages: props.messages,
    liveSteps: props.liveSteps,
    streaming: props.streaming,
    endedSteps: endedSteps.value,
    awaiting: !props.streamingText,
    turnKey: turnKey.value,
    keyAliases: keyAliases.value,
  }),
)
</script>

<template>
  <template v-for="row in rows" :key="row.key">
    <AppMessage v-if="row.kind === 'activity'" :from="row.from">
      <ActivityThread :items="row.items" :duration="row.duration" :is-streaming="row.streaming" />
    </AppMessage>
    <AppMessage
      v-else-if="row.kind === 'message'"
      :from="row.message.role"
      :attachments="row.message.attachments"
      :context="row.message.context"
      :arriving="isPendingMessage(row.message)"
    >
      <AppResponse v-if="row.message.role === 'assistant'" :content="row.message.text" />
      <template v-else-if="row.message.text">{{ row.message.text }}</template>
    </AppMessage>
    <AppMessage v-else-if="row.kind === 'outcomes'" :from="row.from">
      <AppOutcomes :outcomes="row.outcomes" />
    </AppMessage>
    <AppMessage v-else-if="row.kind === 'designEdit'" :from="row.from">
      <DesignEditCard :design-id="row.designId" :run-id="row.runId" />
    </AppMessage>
    <AppMessage v-else :from="row.from">
      <AppSources :sources="row.sources" @open="$emit('openKnowledge', $event)" />
    </AppMessage>
  </template>

  <template v-if="streaming">
    <AppMessage v-if="streamingText" from="assistant">
      <AppResponse :content="streamingText" streaming />
    </AppMessage>
    <AppMessage v-if="liveOutcomeRows.length" from="assistant">
      <AppOutcomes :outcomes="liveOutcomeRows" />
    </AppMessage>
    <AppMessage v-for="edit in liveDesignEdits" :key="`design-edit:${edit.runId}`" from="assistant">
      <DesignEditCard :design-id="edit.designId" :run-id="edit.runId" />
    </AppMessage>
  </template>

  <AppMessage v-if="settledActionAsk" from="assistant">
    <AgentAsk :ask="settledActionAsk" />
  </AppMessage>

  <AppMessage v-if="settledQuestionAsk" from="assistant">
    <AgentAsk :ask="settledQuestionAsk" />
  </AppMessage>

  <AppMessage v-if="settledConnectAsk" from="assistant">
    <AgentAsk :ask="settledConnectAsk" />
  </AppMessage>
</template>
